import React from "react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Clock, LogOut, Warehouse, XCircle } from "lucide-react";

export default function Pending() {
  const { user, logout, refresh } = useAuth();
  const [checking, setChecking] = React.useState(false);
  if (!user) return null;
  const rejected = user.status === "rejected";

  async function onRefresh() {
    setChecking(true);
    try {
      await refresh();
      toast.info("No change yet — you're still pending approval.");
    } finally {
      // Auth router redirects immediately if status becomes 'approved', so the
      // toast above only fires when the user is still pending after refresh.
      setChecking(false);
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md text-center">
        <div className="inline-flex items-center gap-2 mb-8">
          <Warehouse className="w-6 h-6 text-primary" />
          <span className="font-mono text-sm uppercase tracking-[0.2em]">rackworks</span>
        </div>
        <div className="border border-border/60 rounded-sm bg-card p-8 space-y-5">
          <div className={`inline-flex w-12 h-12 rounded-sm items-center justify-center ${rejected ? "bg-destructive/15 text-destructive" : "bg-amber-500/15 text-amber-500"}`}>
            {rejected ? <XCircle className="w-6 h-6" /> : <Clock className="w-6 h-6" />}
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">// account status</div>
            <h1 data-testid="pending-heading" className="text-2xl font-bold tracking-tight mt-1">
              {rejected ? "Access rejected" : "Waiting for approval"}
            </h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-xs mx-auto leading-relaxed">
              {rejected
                ? "Your access request was rejected. Please contact your administrator if you believe this is a mistake."
                : "Thanks for registering. An administrator needs to approve your account before you can access the layout designer."}
            </p>
          </div>
          <div className="border-t border-border/60 pt-4 space-y-1.5 text-left">
            <div className="flex justify-between font-mono text-xs">
              <span className="text-muted-foreground">Signed in as</span>
              <span data-testid="pending-email">{user.email}</span>
            </div>
            <div className="flex justify-between font-mono text-xs">
              <span className="text-muted-foreground">Requested role</span>
              <span className="uppercase">{user.role}</span>
            </div>
            <div className="flex justify-between font-mono text-xs">
              <span className="text-muted-foreground">Status</span>
              <span data-testid="pending-status" className={rejected ? "text-destructive font-semibold uppercase" : "text-amber-500 font-semibold uppercase"}>
                {user.status}
              </span>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button data-testid="pending-refresh" variant="outline" disabled={checking} className="flex-1 rounded-sm h-9 font-mono uppercase tracking-widest text-[11px]" onClick={onRefresh}>
              {checking ? "Checking…" : "Check status"}
            </Button>
            <Button data-testid="pending-logout" variant="ghost" className="rounded-sm h-9 font-mono uppercase tracking-widest text-[11px]" onClick={logout}>
              <LogOut className="w-3.5 h-3.5 mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
